import { inject, Injectable } from '@angular/core';
import {
  BackendService,
  BaseApiService,
  ERole,
  ID,
  RequestFacadeModel,
  RequestModel,
  RequestType,
} from '@cf/core';
import { TranslateService } from '@tolgee/ngx';
import { Observable } from 'rxjs';
import { IMeetingEvent } from '../../meeting/interfaces/meeting-event.interface';
import { SchemaAppEventDetail } from '../../schema';
import { IAppEvent } from '../interfaces';

@Injectable({
  providedIn: 'root',
})
export class AppEventApiService extends BaseApiService {
  translateService = inject(TranslateService);

  constructor(backendService: BackendService) {
    super(backendService, 'app-events');
  }

  getEvents(query: Record<string, string>): Observable<{ count: number; results: IAppEvent[] }> {
    const request = new RequestModel<{ count: number; results: IAppEvent[] }>({
      url: this.getFullUrl(''),
    }).withQuery(query);
    const requestFacade = new RequestFacadeModel<{ count: number; results: IAppEvent[] }>({
      requestType: RequestType.get,
      request,
    });
    return this.send<any, { count: number; results: IAppEvent[] }>(requestFacade);
  }

  event(token: string, role?: ERole): Observable<SchemaAppEventDetail> {
    const request: RequestModel = new RequestModel<null>({
      url: this.getFullUrl(`${token}`),
      redirectOnError: 'error/404',
      skipNotify: true,
    }).withQuery({ role });
    const requestFacade: RequestFacadeModel = new RequestFacadeModel<null>({
      requestType: RequestType.get,
      request,
    });
    return this.send<SchemaAppEventDetail, null>(requestFacade);
  }

  rescheduleEvent(token: string, dto: any): Observable<SchemaAppEventDetail> {
    const request: RequestModel = new RequestModel<any>({
      url: this.getFullUrl(`${token}/reschedule`),
      successMessage: {
        title: this.translateService.instant('event_notifications.title'),
        message: this.translateService.instant('event_notifications.rescheduled'),
      },
      requestBody: dto,
    });
    const requestFacade: RequestFacadeModel = new RequestFacadeModel<any>({
      requestType: RequestType.post,
      request,
    });
    return this.send<SchemaAppEventDetail, any>(requestFacade);
  }

  cancelEvent(token: string | number, notes: string): Observable<any> {
    const request: RequestModel = new RequestModel<any>({
      url: this.getFullUrl(`${token}/cancel`),
      successMessage: {
        title: this.translateService.instant('event_notifications.title'),
        message: this.translateService.instant('event_notifications.cancelled'),
      },
      requestBody: { notes },
    });
    const requestFacade: RequestFacadeModel = new RequestFacadeModel<any>({
      requestType: RequestType.post,
      request,
    });
    return this.send<any, any>(requestFacade);
  }

  acceptTentative(token: ID, skipNotify = false): Observable<IMeetingEvent> {
    const request: RequestModel = new RequestModel<any>({
      url: this.getFullUrl(`${token}/accept`),
      skipNotify,
    });
    const requestFacade: RequestFacadeModel = new RequestFacadeModel<any>({
      requestType: RequestType.post,
      request,
    });
    return this.send<any, any>(requestFacade);
  }

  rejectTentative(
    token: ID,
    rejection_reason: string,
    skipNotify = false,
  ): Observable<IMeetingEvent> {
    const request: RequestModel = new RequestModel<any>({
      url: this.getFullUrl(`${token}/reject`),
      requestBody: { rejection_reason },
      skipNotify,
    });
    const requestFacade: RequestFacadeModel = new RequestFacadeModel<any>({
      requestType: RequestType.post,
      request,
    });
    return this.send<any, any>(requestFacade);
  }
}
